import React, { useState } from 'react'
import { useFormContext, useFieldArray } from 'react-hook-form'
import Box from '@material-ui/core/Box'
import FormSubheader from '../create-profile-form/form-subheader'
import { makeStyles } from '@material-ui/core/styles'
import { ErrorMsg } from 'components/coaches-profile/profile.styles'
import Button from '@material-ui/core/Button'
import TonalButton from 'components/empty-page-state/tonal-button'
import FileUploadOutlinedIcon from 'components/material-symbol/icons/FileUpload'
import DeleteOutlinedIcon from 'components/material-symbol/icons/Delete'
import { IconTrashcan } from 'components/atoms/icons'
import Checkbox from '@material-ui/core/Checkbox'
import IconButton from '@material-ui/core/IconButton'
import uploadImage from 'lib/cloudinary/uploadImage'
import { CLOUDINARY_CLOUDNAME, CLOUDINARY_UPLOAD_PRESET_LARGE } from 'env'
import {
  OUTLINED_INPUT_BORDER,
  OUTLINED_INPUT_BORDER_HOVER,
  OUTLINED_INPUT_BORDER_RADIUS,
} from 'lib/form-field-styles'
import { useCreateProfileFieldStyles } from '../create-profile-form/field-styles'

const useStyles = makeStyles(theme => ({
  photoWrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
    gap: theme.spacing(2),
    width: '100%',
  },
  photoCard: {
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${OUTLINED_INPUT_BORDER}`,
    borderRadius: OUTLINED_INPUT_BORDER_RADIUS,
    overflow: 'hidden',
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      borderColor: OUTLINED_INPUT_BORDER_HOVER,
    },
  },
  photoCardSelected: {
    borderColor: '#0053FF',
    boxShadow: '0 0 0 1px #0053FF',
  },
  photoImage: {
    width: '100%',
    aspectRatio: '1.7 / 1',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
  },
  photoActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 0.75, 0.75),
  },
  photoDeleteButton: {
    padding: theme.spacing(0.75),
    color: '#172024',
    '&:hover': {
      backgroundColor: 'rgba(23, 32, 36, 0.04)',
    },
    '& svg': {
      fontSize: 20,
    },
  },
}))

const MAX_PHOTOS_COUNT = 8
const photosMsg = `Add up to ${MAX_PHOTOS_COUNT} photos.`

const uploadOptions = {
  cloudName: CLOUDINARY_CLOUDNAME,
  upload_preset: CLOUDINARY_UPLOAD_PRESET_LARGE,
  cropping: true,
  multiple: false,
  croppingAspectRatio: 1.7,
  showSkipCropButton: false,
  sources: ['local', 'camera'],
  resourceType: 'image',
  folder: 'dot-com-images',
  showPoweredBy: false,
}

export const Photos = () => {
  const classes = useStyles()
  const fieldClasses = useCreateProfileFieldStyles()
  const { control, register } = useFormContext()
  const { fields, append, remove } = useFieldArray({
    name: 'photosJsonData',
    control,
  })
  const [uploadError, setUploadError] = useState(null)
  const [checkedPhotos, setCheckedPhotos] = useState([])

  const handleImageUpload = async () => {
    try {
      const ref = await uploadImage(uploadOptions)
      append({ rank: fields.length + 1, reference: ref })
      setUploadError(null)
    } catch (e) {
      setUploadError(e.statusText)
    }
  }

  const handleCheckboxToggle = (isChecking, rank) => {
    if (isChecking) {
      setCheckedPhotos(photos => [rank, ...photos])
    } else {
      const rankIndex = checkedPhotos.findIndex(photo => photo === rank)
      const checked = [...checkedPhotos]
      if (rankIndex !== -1) {
        checked.splice(rankIndex, 1)
        setCheckedPhotos(checked)
      }
    }
  }

  const handlePhotosDelete = deleteIndex => {
    if (deleteIndex >= 0) {
      remove(deleteIndex)
    } else {
      checkedPhotos.forEach(rank => {
        remove(rank - 1)
      })
      setCheckedPhotos([])
    }
  }

  return (
    <Box className={fieldClasses.fieldSection}>
      <FormSubheader title="Photos" description={photosMsg} />
      <Box className={fieldClasses.fieldStack}>
        {uploadError ? <ErrorMsg>{uploadError}</ErrorMsg> : null}
        {fields.length > 0 ? (
          <Box className={classes.photoWrapper}>
            {fields.map((photo, i) => {
              const isSelected = Boolean(
                checkedPhotos.find(pic => pic === photo.rank),
              )

              return (
                <Box
                  key={photo.id}
                  className={`${classes.photoCard} ${
                    isSelected ? classes.photoCardSelected : ''
                  }`}
                  onClick={() => handleCheckboxToggle(!isSelected, photo.rank)}
                >
                  <Box
                    className={classes.photoImage}
                    style={{ backgroundImage: `url(${photo.reference})` }}
                    role="img"
                    aria-label="Photo preview"
                  />
                  <Box className={classes.photoActions}>
                    <Checkbox
                      checked={isSelected}
                      color="default"
                      size="small"
                      classes={{
                        root: fieldClasses.selectionCheckboxRoot,
                        checked: fieldClasses.selectionCheckboxChecked,
                      }}
                      onClick={event => event.stopPropagation()}
                      onChange={event => {
                        event.stopPropagation()
                        handleCheckboxToggle(event.target.checked, photo.rank)
                      }}
                    />
                    <IconButton
                      className={classes.photoDeleteButton}
                      aria-label="Delete photo"
                      onClick={event => {
                        event.stopPropagation()
                        handlePhotosDelete(i)
                      }}
                    >
                      <DeleteOutlinedIcon fontSize="small" opticalSize={20} />
                    </IconButton>
                  </Box>
                  <input
                    type="hidden"
                    ref={register()}
                    name={`photosJsonData[${i}].rank`}
                    value={i + 1}
                  />
                  <input
                    type="hidden"
                    ref={register()}
                    name={`photosJsonData[${i}].reference`}
                    value={photo.reference}
                  />
                </Box>
              )
            })}
          </Box>
        ) : null}
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          style={{ gap: 16 }}
        >
          <TonalButton
            type="button"
            startIcon={<FileUploadOutlinedIcon fontSize="small" />}
            onClick={() => handleImageUpload()}
            disabled={fields.length >= MAX_PHOTOS_COUNT}
          >
            Upload Photo
          </TonalButton>
          {checkedPhotos.length > 0 ? (
            <Button
              className={fieldClasses.repeaterDeleteButtonFilled}
              variant="contained"
              color="default"
              disableElevation
              size="large"
              type="button"
              startIcon={<IconTrashcan />}
              onClick={handlePhotosDelete}
            >
              Delete
            </Button>
          ) : null}
        </Box>
      </Box>
    </Box>
  )
}

export default Photos
